import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { OpsApi } from "../useOps";
import type { ChatMessage } from "@/types/api";
import { config } from "@/config";
import { getIdToken } from "@/lib/firebase";

/** Desk chat — one room for everyone on the desk, docked bottom-right of the ops board.
 *  Live messages ride the primary WS; history backfills via GET /api/chat; system messages
 *  (new high/critical signals) anchor the conversation and click through to the signal. */

const OPEN_KEY = "ew.chatOpen";

export function ChatDrawer({ ops }: { ops: OpsApi }) {
  const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) === "1");
  const [unread, setUnread] = useState(0);
  const [input, setInput] = useState("");
  const [handle, setHandle] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  // Handle is chosen once at first login (HandleGate) — pick up the fresh value when it saves.
  useEffect(() => {
    const h = (e: Event) => setHandle((e as CustomEvent<string>).detail);
    window.addEventListener("ew:handle", h);
    return () => window.removeEventListener("ew:handle", h);
  }, []);
  const listRef = useRef<HTMLDivElement | null>(null);
  const seeded = useRef(false);
  const openRef = useRef(open);
  openRef.current = open;

  const client = ops.getClient();
  const msgs: ChatMessage[] = client?.chat ?? [];

  // History backfill once the client exists.
  useEffect(() => {
    if (!client || seeded.current) return;
    seeded.current = true;
    (async () => {
      try {
        const token = await getIdToken();
        const r = await fetch(`${config.restUrl}/api/chat?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const j = (await r.json()) as { messages?: ChatMessage[]; handle?: string | null };
        client.seedChat(j.messages ?? []);
        setHandle(j.handle ?? null);
      } catch { /* room still works live-only */ }
    })();
  }, [client]);

  // Unread badge + autoscroll on every chat change.
  useEffect(() => {
    if (openRef.current) {
      const el = listRef.current;
      if (el) el.scrollTop = el.scrollHeight;
      setUnread(0);
    } else if (ops.chatTick > 0) {
      setUnread((u) => u + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ops.chatTick]);

  function toggle() {
    const next = !open;
    setOpen(next);
    localStorage.setItem(OPEN_KEY, next ? "1" : "0");
    if (next) {
      setUnread(0);
      requestAnimationFrame(() => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; });
    }
  }

  function send() {
    const body = input.trim();
    if (!body || !client) return;
    if (!client.sendChat(body)) { setHint("reconnecting — try again in a second"); return; }
    setInput("");
    setHint(null);
  }

  return (
    <div className="chatdock">
      {open && (
        <div className="chatpanel">
          <div className="chead">
            <span className="t">DESK CHAT</span>
            <span className="hbtn" style={{ cursor: "default" }} title="Your permanent operator handle">
              {handle ?? "…"}
            </span>
          </div>
          <div className="clist" ref={listRef}>
            {msgs.length === 0 && <div className="cempty">Quiet desk. Signals post here as they fire — say hello.</div>}
            {msgs.map((m) => (
              <div key={m.id} className={`cmsg ${m.kind}`}>
                <span className="ch">{m.handle}</span>
                {m.kind === "system" && m.signalId ? (
                  <span className="cb link" onClick={() => ops.selectSignal(m.signalId!)}>{m.body}</span>
                ) : (
                  <span className="cb">{m.body}</span>
                )}
                <span className="ct">{new Date(m.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
          {hint && <div className="chint">{hint}</div>}
          <div className="cinput">
            <input
              value={input}
              maxLength={500}
              placeholder="message the desk…"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            />
            <button onClick={send} disabled={!input.trim()}>SEND</button>
          </div>
          <div className="cfoot">No MNPI, no manipulation — <Link to="/terms">chat rules</Link>. Messages visible to all operators.</div>
        </div>
      )}
      <button className={`chatfab${unread > 0 ? " has" : ""}`} onClick={toggle} title="Desk chat">
        DESK{unread > 0 ? <span className="badge">{unread > 99 ? "99+" : unread}</span> : null}
      </button>
    </div>
  );
}
